import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Table, Callout, Ext, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Tribys Privacy Policy (English).
//
// Every factual claim here is meant to be checkable against the code. The data
// categories and the retention table are derived from
// tribys_back/src/services/db/personalData/personalDataDomains.ts — the register
// that also drives the in-app export and the deletion job. If you add a table
// holding personal data, it needs an entry there AND a line here.
//
// Do not add claims about detection or protections we do not actually run. In
// particular: known-CSAM hash matching is not implemented (the detector is a
// stub), so nothing in this document may imply that it is.

export const privacyEn: LegalDoc = {
  lang: 'en',
  title: 'Privacy Policy',
  updatedLabel: `Last updated ${entity.effectiveDate}`,
  contentsLabel: 'Contents',
  altHref: legalPaths.privacyEs,
  altLabel: 'Ver en español',
  sections: [
    {
      id: 'who-we-are',
      heading: '1. Who we are and what this covers',
      body: (
        <>
          <P>
            Tribys is an app for finding small groups of people who share your
            interests and meeting them in real life. This policy explains what
            personal information we collect, why, who we give it to, how long we
            keep it, and what you can do about it.
          </P>
          <P>
            The company responsible for your information (the &quot;data
            controller&quot;, or <em>responsable</em> under Mexican law) is{' '}
            <strong><Tok value={entity.legalName} /></strong>, a limited
            liability company formed in <Tok value={entity.formedIn} />, at{' '}
            <Tok value={entity.address} />. We are reachable at{' '}
            <Tok value={entity.privacyEmail} />.
          </P>
          <P>
            This policy covers the Tribys mobile app, the tribys.com website, and
            our backend services. It does not cover a third-party service you
            choose to connect or open from Tribys — those are governed by their
            own policies.
          </P>
          <P>
            Two related documents sit alongside this one. Our{' '}
            <a href={legalPaths.biometric}>
              Biometric &amp; Consumer Health Data Notice
            </a>{' '}
            covers identity verification and face matching in detail, and is
            published separately because Washington and Illinois law require it
            to be. Our Terms of Service and Community Guidelines govern conduct
            rather than data.
          </P>
        </>
      ),
    },
    {
      id: 'age',
      heading: '2. You must be 18 or older',
      body: (
        <>
          <P>
            Tribys is for adults. You must be at least 18 to create an account,
            and we ask for your date of birth during onboarding to check. Every
            group has a minimum age of 18 as well.
          </P>
          <P>
            We do not knowingly collect information from anyone under 18. If you
            believe a minor has an account, write to{' '}
            <Tok value={entity.privacyEmail} /> and we will remove it.
          </P>
        </>
      ),
    },
    {
      id: 'collect',
      heading: '3. What we collect',
      body: (
        <>
          <P>
            We collect only what the app needs to work. There is no advertising
            network, no analytics SDK, and no data broker anywhere in Tribys —
            see section 8.
          </P>
          <Table
            head={['Category', 'What that means specifically', 'Where it comes from']}
            rows={[
              [
                'Account and login',
                'Username, email address and/or phone number, and a password (stored only as a hash — we never hold the password itself). If you sign in with Google or Apple, the account identifier and the email address that provider returns to us. Six-digit one-time codes when you ask for one.',
                'You, or the sign-in provider you chose',
              ],
              [
                'Profile',
                'Full name, date of birth, gender, a short bio, relationship status, personality type, languages you speak, and at least five interests. If you connect Spotify, your display name and the artists you choose to show.',
                'You',
              ],
              [
                'Identity verification (optional)',
                'If you choose to verify: the name, date of birth and gender read from your government ID, what kind of document it was, and one still reference image of your face. We do not keep the ID document image, its number, any personal or national identification number on it, or the verification video.',
                'You, via our verification processor',
              ],
              [
                'Location',
                'A single current location — either a position from your device or a pin you place on the map yourself. We keep the latest point only, not a history or a trail. We also derive an approximate city-level location from your IP address.',
                'Your device, or you',
              ],
              [
                'Things you create',
                'Chat messages, group titles and descriptions, spot names, descriptions, addresses and exact coordinates, photos you upload, and the text of any report you file.',
                'You',
              ],
              [
                'Your connections',
                'Friendships and friend requests, group memberships and invitations, and the list of accounts you have blocked.',
                'You and other members',
              ],
              [
                'Device',
                'A push notification token, an installation identifier, and whether you are on iOS or Android.',
                'Your device',
              ],
              [
                'Purchases',
                'Which product you bought, when, and the transaction identifiers Apple or Google give us. We never see or store your card details — Apple and Google handle payment entirely.',
                'Apple, Google',
              ],
              [
                'Technical logs',
                'Your account identifier, IP address and timestamps for requests to our servers, kept for security and debugging.',
                'Automatically',
              ],
              [
                'Derived information',
                'Your age (calculated from your date of birth), automated content-moderation results, and the match and ranking data that decides which groups appear in search and which notifications you receive.',
                'Generated by us',
              ],
            ]}
          />
        </>
      ),
    },
    {
      id: 'sensitive',
      heading: '4. Sensitive information',
      body: (
        <>
          <P>
            Three kinds of information we handle get extra protection under US
            state law, and we treat them accordingly.
          </P>
          <H3>Precise location</H3>
          <P>
            Location is central to Tribys — the app is a map, and finding a group
            near you is the entire point. So you should know exactly how it
            works:
          </P>
          <Ul
            items={[
              'We read your location only while the app is open and in the foreground. We never track you in the background, and the app does not request background-location permission.',
              'We send an updated position to our servers at most once every 15 minutes, and only if you have moved more than about 500 metres.',
              'We store one current point, overwritten each time. We do not build a location history.',
              'If you decline location permission, the app still works: you can browse without location, or place a pin on the map to say roughly where you want to look.',
              'Other members never see your coordinates. They see a rounded distance — "less than 1 km away", "6 km away" — and nothing more.',
            ]}
          />
          <P>
            Spots are the exception, and deliberately so: a spot is a public
            place, and its name, address and exact coordinates are visible to
            everyone, including on the public web. Do not create a spot at your
            home.
          </P>
          <H3>Identity and biometric information</H3>
          <P>
            Verification is optional and you can skip it. If you use it, we
            process an image of your face and compare later profile photos
            against it. Because that is biometric information, it has its own
            notice: see the{' '}
            <a href={legalPaths.biometric}>
              Biometric &amp; Consumer Health Data Notice
            </a>
            .
          </P>
          <H3>What your interests may reveal</H3>
          <P>
            Interests are free-form and members can create their own. An interest
            or a group name you choose might imply something about your beliefs,
            your politics, your health or your sexuality. We want to be direct
            about how we handle that:
          </P>
          <Ul
            items={[
              'We use interests only to match you with groups and to rank search results. We do not analyse them to infer your health, religion, politics or sexual orientation, and we do not build profiles of that kind.',
              'We do not sell or share interests, or anything else, for advertising.',
              'But interests you put on your profile are visible to other members, and appear on public group pages. If an interest is something you would rather not publish, do not add it.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'use',
      heading: '5. How we use your information',
      body: (
        <>
          <Ul
            items={[
              <><strong>To run your account</strong> — signing you in, verifying your email or phone, keeping your session alive, and restoring your account if you change your mind about deleting it.</>,
              <><strong>To show you groups and spots near you</strong> — searching by map area and distance. On a free account, search is limited to about 10 km around you; with Pro it extends to about 80 km.</>,
              <><strong>To let you communicate</strong> — direct and group chat, friend requests, invitations, and notifications about them.</>,
              <><strong>To tell you about relevant new groups</strong> — when a new public group is created, we look for members within about 80 km whose age, gender and interests fit the group, and send them a notification. You can turn this off per notification type in Settings.</>,
              <><strong>To keep Tribys safe</strong> — automated checks on photos and text, handling reports and blocks, and enforcing bans. See section 6.</>,
              <><strong>To handle purchases</strong> — validating receipts with Apple or Google, tracking your subscription state, and issuing referral rewards.</>,
              <><strong>To keep the service working and secure</strong> — rate limiting, abuse prevention, error monitoring and debugging.</>,
              <><strong>To meet legal obligations</strong> — keeping financial records, and responding to lawful requests.</>,
            ]}
          />
          <P>
            We do not use your information to train machine-learning models, and
            we do not send it to any general-purpose AI service.
          </P>
        </>
      ),
    },
    {
      id: 'automated',
      heading: '6. Automated decisions, and how to reach a person',
      body: (
        <>
          <P>
            Some decisions in Tribys are made automatically, without a human
            looking first. You are entitled to know what they are.
          </P>
          <Ul
            items={[
              <><strong>Photo moderation.</strong> Every profile photo and spot photo is checked automatically before it appears. A photo is rejected if it is detected as sexual or otherwise unsafe, if a profile photo contains no detectable face, or — if you are verified — if the face does not match your verification image. The app tells you which of these happened.</>,
              <><strong>Text moderation.</strong> Your name, bio, group titles and descriptions, spot details and any new interest you create are checked for toxic content when you save them, and are rejected outright above a confidence threshold. Private chat messages are <em>not</em> put through this check.</>,
              <><strong>Matching and notifications.</strong> Who gets told about a new group is decided automatically from location, age, gender and interests, as described in section 5.</>,
              <><strong>Search ranking.</strong> The order of search results is computed automatically, and group owners can pay to boost a group&apos;s position.</>,
            ]}
          />
          <Callout>
            If an automated decision went against you and you think it was wrong,
            email <Tok value={entity.privacyEmail} /> and a person will review
            it. You have this right explicitly under Quebec law, and we extend it
            to everyone.
          </Callout>
          <P>
            Bans are not automatic. They are issued by a human moderator, carry a
            stated reason, and may be temporary or permanent. There is no
            automatic strike system.
          </P>
        </>
      ),
    },
    {
      id: 'share',
      heading: '7. Who we share information with',
      body: (
        <>
          <P>
            We do not sell your information. We do use other companies to run
            Tribys, and they only handle your information to do work for us, under
            contract and on our instructions.
          </P>
          <P>
            We name them rather than describing them vaguely, because you should
            be able to see exactly who is involved. This list is accurate as of
            the date at the top of this page. We may add or replace a provider as
            the service changes, and when we do we will update this list and the
            date — so if a provider matters to you, check back here.
          </P>
          <Table
            head={['Who', 'What they do', 'What they receive']}
            rows={[
              [
                'Amazon Web Services',
                'Storage, image analysis, text analysis, email and SMS delivery, and our image CDN',
                'Photos and verification images; the text of your name, bio, group and spot details; your email address or phone number and the codes we send you',
              ],
              [
                'Google Cloud',
                'Hosting our backend and storing configuration secrets',
                'Anything that passes through the API, plus request logs containing your account identifier and IP address',
              ],
              [
                'Neon',
                'Our managed database',
                'The database as a whole',
              ],
              [
                'Upstash',
                'Real-time messaging between our servers, and rate limiting',
                'Live event payloads, and rate-limit keys that can include your email address or phone number',
              ],
              [
                'Didit',
                'Identity verification',
                'Your ID document, selfie and verification video. Covered in the separate biometric notice',
              ],
              [
                'Google (Firebase) and Apple',
                'Delivering push notifications',
                'Your device token, and the notification itself — which includes the sender name and up to 120 characters of a chat message',
              ],
              [
                'Google Maps',
                'Showing maps in the app and on the website',
                'The map area being viewed',
              ],
              [
                'Sentry',
                'Error and performance monitoring',
                'Your account identifier and technical error details. We turn off its personal-data collection and strip out IP addresses',
              ],
              [
                'Slack',
                'Our internal moderation channel',
                'When you file a report: your username, account identifier, email address and phone number, along with the report itself',
              ],
              [
                'Klipy',
                'The GIF and sticker picker in chat',
                'What you type into the GIF search box, sent from your device directly to them',
              ],
              [
                'Google Workspace',
                'Our company email',
                'Whatever you send us when you contact us — including a privacy or deletion request, since those arrive by email',
              ],
              [
                'Spotify',
                'The optional Spotify connection',
                'Authorisation to read your top artists, if you connect it',
              ],
              [
                'Apple and Google',
                'Sign-in, and all payments as merchant of record',
                'Sign-in tokens; and, for purchases, everything about the transaction',
              ],
            ]}
          />
          <P>
            We will also disclose information if the law requires it — a valid
            legal request, a court order — or where we genuinely need to in order
            to protect someone&apos;s safety or defend our legal rights. If
            Tribys is ever sold or merged, information transfers with it, and we
            will say so here beforehand.
          </P>
          <H3>Information that leaves through your own device</H3>
          <P>
            Some features hand data to software we do not control, at your
            request:
          </P>
          <Ul
            items={[
              'Adding a group to your calendar writes its title, time and location into whatever calendar account your device uses — which may sync it to Google, Apple or your employer.',
              'Opening a spot in a maps app passes its coordinates to that app.',
              'Sharing a link sends it to whichever app you pick.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'no-sale',
      heading: '8. We do not sell your information, and there are no ads or analytics',
      body: (
        <>
          <Callout>
            <strong>
              We have never sold personal information, and we do not share it for
              cross-context behavioural advertising.
            </strong>{' '}
            Tribys contains no advertising SDK, no analytics SDK, no attribution
            or install-tracking SDK, and no advertising identifier is collected.
          </Callout>
          <P>
            This is a deliberate architectural choice, not just a promise. There
            is no Google Analytics, Firebase Analytics, Mixpanel, Amplitude,
            Segment, PostHog, AppsFlyer, Adjust or Branch anywhere in the app.
          </P>
          <P>
            We also do not use your information for profiling that produces legal
            or similarly significant effects, and we do not engage in targeted
            advertising of any kind. That means the opt-outs several US state laws
            provide for selling, sharing, targeted advertising and profiling have
            nothing to switch off — but you can still exercise them, and we honour
            opt-out preference signals such as Global Privacy Control.
          </P>
        </>
      ),
    },
    {
      id: 'retention',
      heading: '9. How long we keep information',
      body: (
        <>
          <Table
            head={['Information', 'How long']}
            rows={[
              [
                'Account and profile',
                'While your account exists. If you delete it, we scrub it after the 30-day grace period described in section 11.',
              ],
              [
                'Verification data',
                'Our verification processor deletes its copy — including your ID document and video — after 6 months. Our own retention and destruction schedule is set out in the separate biometric notice.',
              ],
              [
                'Chat messages',
                'While the conversation exists. When you delete your account the content of your messages is blanked, but the message record remains so the other person\'s conversation is not destroyed. Group chats close 3 days after the meetup.',
              ],
              [
                'Photos',
                'While in use. Unused or archived images are deleted after 12 months. A raw upload that never finishes processing is deleted within about a day.',
              ],
              [
                'Location',
                'One current point, overwritten each update, and erased when you delete your account.',
              ],
              [
                'Data export files',
                'The download link works for 1 hour; the file itself is deleted automatically after 7 days.',
              ],
              [
                'Payment records',
                'Kept after account deletion, for as long as tax, accounting and fraud-prevention obligations require — generally around 7 years.',
              ],
              [
                'Reports, blocks and bans',
                'Kept after account deletion, because deleting them would let a banned or abusive account reset itself, and we may need them to defend a legal claim.',
              ],
              [
                'Groups, spots, memberships, friendships and referral records',
                'Kept, because other members depend on them. Once your account record is scrubbed these no longer identify you.',
              ],
              [
                'Preferences and activity records',
                'Your chat settings, read positions, notifications, notification preferences, interests and languages are kept after deletion. They are low-sensitivity and, once your account record is scrubbed, are linked only to an anonymised identifier.',
              ],
              [
                'Server logs',
                'A short rolling window for security and debugging, currently 30 days.',
              ],
              [
                'Device push tokens',
                'Deleted when you sign out, when you delete your account, or when the push service tells us the token is dead.',
              ],
            ]}
          />
        </>
      ),
    },
    {
      id: 'security',
      heading: '10. How we protect your information',
      body: (
        <>
          <Ul
            items={[
              'Everything is encrypted in transit (TLS 1.2 or better) and at rest.',
              'Tokens we hold for other services — your Spotify connection, the Apple token we keep so we can revoke it when you leave — are separately encrypted with AES-256-GCM.',
              'Photos and verification images live in private storage that is not publicly listable; uploads are not served from our CDN until they have passed moderation.',
              'Our servers authenticate to other clouds using short-lived federated credentials. We store no long-lived cloud access keys anywhere.',
              'Each backend service runs with only the permissions it needs and can read only the secrets it declares.',
              'Administrative tools are restricted to an approved IP range and company accounts.',
              'Passwords are stored only as hashes. Nobody at Tribys can read your password.',
            ]}
          />
          <P>
            No system is perfectly secure. If we discover a breach affecting your
            information, we will notify you and the relevant authorities as the
            law requires.
          </P>
        </>
      ),
    },
    {
      id: 'controls',
      heading: '11. Your controls inside the app',
      body: (
        <>
          <P>
            Most of what you might want to do, you can do yourself, without
            asking us:
          </P>
          <Ul
            items={[
              <><strong>Download your data.</strong> Settings → Export My Data produces a JSON file with the personal data we hold about you.</>,
              <><strong>Delete your account.</strong> Settings → Delete Account. See section 12 for exactly what that does.</>,
              <><strong>Edit or correct your profile</strong> at any time. Note that once you verify your identity, your name, date of birth and gender are locked to what your ID said.</>,
              <><strong>Turn notifications off</strong> individually, by type, in Settings → Notification Preferences.</>,
              <><strong>Block and report</strong> other members. Blocking is mutual: neither of you sees the other&apos;s full profile or the groups they join.</>,
              <><strong>Mute a conversation</strong>, or remove it from your own device.</>,
              <><strong>Control location.</strong> Deny or revoke the permission in your device settings and keep using the app, or set your area with a pin instead.</>,
              <><strong>Skip identity verification</strong> entirely. It is optional.</>,
            ]}
          />
          <P>
            If you would rather we did something on your behalf, or you want to
            exercise a right that has no button, email{' '}
            <Tok value={entity.privacyEmail} />. We do not charge for this, and we
            will not treat you differently for asking.
          </P>
        </>
      ),
    },
    {
      id: 'deletion',
      heading: '12. What happens when you delete your account',
      body: (
        <>
          <P>
            When you delete your account we schedule it for removal in 30 days and
            immediately hide it from everyone else. During those 30 days you can
            sign back in and restore it. After that, the removal runs
            automatically and cannot be undone.
          </P>
          <P>At that point:</P>
          <Ul
            items={[
              'Your email address, phone number, password, and any Google or Apple account links are erased, and your username is replaced with an anonymous placeholder.',
              'Your name, date of birth, gender, bio, relationship status and personality type are erased.',
              'Your stored location is erased.',
              'Your verification record is deleted, your profile photos are deleted from our storage, and your verification image is deleted from our storage.',
              'We instruct our verification processor to delete its copy of your verification data.',
              'If you signed in with Apple, we revoke the Apple sign-in token so that link is severed at Apple as well.',
              'Your Spotify connection and its stored tokens are deleted.',
              'The content of your chat messages is blanked.',
            ]}
          />
          <P>
            We are being precise about one thing on purpose: we do not delete the
            underlying account row. It is stripped of everything that identifies
            you and marked as anonymised, but it stays, because groups, spots and
            conversations that other people are part of point at it. Deleting it
            outright would delete other members&apos; content and their side of
            shared conversations.
          </P>
          <P>
            As set out in section 9, payment records, and reports, blocks and bans
            also survive deletion — the first because we are legally required to
            keep financial records, the second because otherwise deleting an
            account would be a way to erase a ban or a safety record.
          </P>
        </>
      ),
    },
    {
      id: 'where',
      heading: '13. Where your information is stored',
      body: (
        <>
          <P>
            Tribys runs entirely in the <strong>United States</strong>. Our
            servers, our database, our real-time cache and our file storage are
            all in the US Pacific Northwest (Oregon).
          </P>
          <P>
            One exception is worth naming: the content delivery network that
            serves photos caches them at edge locations in{' '}
            <strong>North America and Europe</strong>, so a photo may be
            temporarily cached on a server closer to whoever is viewing it.
          </P>
          <P>
            If you use Tribys from Mexico, Canada or anywhere else, your
            information is transferred to and processed in the United States,
            which may have different privacy protections than your own country.
            By using Tribys you understand this. We rely on contractual
            protections with each of our service providers.
          </P>
        </>
      ),
    },
    {
      id: 'public',
      heading: '14. Information that is public',
      body: (
        <>
          <P>
            Some information is public by design. Be deliberate about it.
          </P>
          <Ul
            items={[
              <><strong>Your profile</strong> — username, name, age, gender, photo, bio, relationship status, personality type, languages, interests and Spotify artists — is visible to other members, and on a public web page if someone has your profile link. Your email address, phone number and location are never shown.</>,
              <><strong>Spots you create</strong> are public, including their exact coordinates, and their pages can be found by search engines.</>,
              <><strong>Group pages</strong> are reachable by anyone with the link and show the group details and the members, including names and photos. They are marked so search engines do not index them.</>,
              <><strong>Link previews.</strong> When a Tribys link is shared into a messaging app, that app fetches a preview containing the name and photo. Any service that scrapes the link can see this.</>,
              <><strong>Upcoming groups on your profile</strong> are visible only to your friends.</>,
            ]}
          />
          <P>
            Profile and group links can be regenerated in the app, which
            immediately breaks every copy of the old link.
          </P>
        </>
      ),
    },
    {
      id: 'changes',
      heading: '15. Changes to this policy',
      body: (
        <P>
          If we change this policy we will update the date at the top. If a change
          materially affects your rights or how we use your information, we will
          tell you in the app or by email before it takes effect. We will not
          apply a materially different use of information you have already given
          us without your consent.
        </P>
      ),
    },
    {
      id: 'contact',
      heading: '16. How to contact us',
      body: (
        <>
          <P>
            Privacy questions and requests: <Tok value={entity.privacyEmail} />
            <br />
            Everything else: <Tok value={entity.supportEmail} />
            <br />
            By post: <Tok value={entity.legalName} />, <Tok value={entity.address} />
          </P>
          <P>
            We answer privacy requests within 45 days, and will tell you if we
            need longer.
          </P>
        </>
      ),
    },
    {
      id: 'us',
      heading: 'Annex A — United States',
      body: (
        <>
          <P>
            This annex applies if you live in a US state with a consumer privacy
            law, including California, Virginia, Colorado, Connecticut, Utah,
            Texas, Oregon, Montana, Delaware, Iowa, Nebraska, New Hampshire, New
            Jersey, Minnesota, Maryland, Tennessee, Indiana, Kentucky and Rhode
            Island. It adds to the rest of this policy.
          </P>
          <H3>Categories, in the statutory language</H3>
          <Table
            head={['Statutory category', 'Do we collect it?', 'Purpose', 'Disclosed to']}
            rows={[
              ['Identifiers (name, email, phone, account ID, IP address)', 'Yes', 'Account, communication, security', 'Hosting, database, email/SMS, error monitoring'],
              ['Customer records (payment transaction records)', 'Yes — no card details', 'Purchases, subscriptions', 'Apple, Google'],
              ['Protected classifications (age, gender)', 'Yes', 'Age eligibility, group matching', 'Hosting, database'],
              ['Commercial information (purchase history)', 'Yes', 'Subscriptions, referral rewards', 'Apple, Google'],
              ['Biometric information (facial image and comparison)', 'Yes — only if you choose to verify', 'Identity verification, impersonation prevention', 'Verification processor, image analysis provider'],
              ['Internet activity (app and API request logs)', 'Yes', 'Security, debugging, rate limiting', 'Hosting, error monitoring'],
              ['Geolocation, including precise', 'Yes', 'Finding nearby groups and spots', 'Hosting, database, maps'],
              ['Audio, electronic, visual information (photos)', 'Yes', 'Profile and spot photos', 'Storage, image analysis, CDN'],
              ['Professional or employment information', 'No', '—', '—'],
              ['Education information', 'No', '—', '—'],
              ['Inferences (interest and match data)', 'Yes', 'Matching, search ranking', 'Hosting, database'],
              ['Sensitive personal information (precise geolocation, biometric information, account log-in credentials)', 'Yes', 'As described above', 'As described above'],
            ]}
          />
          <P>
            Sources are as listed in section 3. Retention is as listed in section
            9. We use sensitive personal information only for the purposes above —
            we do not use or disclose it to infer characteristics about you, so
            the right to limit its use has no additional effect, though you may
            still assert it.
          </P>
          <H3>Your rights</H3>
          <Ul
            items={[
              'Know what we collect, use and disclose, and get a copy.',
              'Get a portable copy of what you provided — the in-app export does this immediately.',
              'Correct inaccurate information.',
              'Delete your information — the in-app Delete Account does this, subject to the retention exceptions in section 9.',
              'Opt out of sale, sharing, targeted advertising and significant profiling. We do none of these, so there is nothing to opt out of, but we honour Global Privacy Control signals regardless.',
              'Limit the use of sensitive personal information.',
              'Not be discriminated against for exercising any of these. We will not degrade your service or charge you more.',
              'Appeal a refused request. Several states require this: reply to our decision and a different reviewer will look again.',
            ]}
          />
          <H3>How to exercise them</H3>
          <P>
            Use the in-app controls in section 11, or email{' '}
            <Tok value={entity.privacyEmail} />. We verify a request by confirming
            you control the account — usually by asking you to send the request
            from the email address or phone number on the account. An authorised
            agent may act for you with written permission that we can verify. We
            respond within 45 days and may extend once when necessary.
          </P>
          <P>
            California residents may also ask what we disclosed in the previous
            calendar year. Because we do not sell or share personal information,
            our answer is nil.
          </P>
        </>
      ),
    },
    {
      id: 'mx',
      heading: 'Annex B — México',
      body: (
        <>
          <P>
            This annex applies if you are in Mexico, under the{' '}
            <em>
              Ley Federal de Protección de Datos Personales en Posesión de los
              Particulares
            </em>
            . A full Spanish version of this notice — the{' '}
            <em>aviso de privacidad integral</em> — is published at{' '}
            <a href={legalPaths.privacyEs}>tribys.com/es/privacy</a>, and the
            Spanish text governs for Mexican users.
          </P>
          <P>
            The <em>responsable</em> is <Tok value={entity.legalName} />, at{' '}
            <Tok value={entity.address} />. The personal data we process are
            enumerated in section 3, and the data identified as{' '}
            <em>sensibles</em> — precise location and biometric data — are set out
            in section 4.
          </P>
          <H3>Necessary and secondary purposes</H3>
          <P>
            <strong>Necessary purposes</strong>, without which the service cannot
            operate: creating and securing your account; showing groups and spots
            near you; messaging; notifications about your own activity; safety and
            moderation; processing purchases; and meeting legal obligations.
          </P>
          <P>
            <strong>Secondary purposes</strong>, which you may refuse without
            losing access to Tribys: identity verification and the biometric
            processing it involves; connecting Spotify; and notifications about new
            groups that match your interests. You can refuse these by skipping
            verification, by not connecting Spotify, and by turning off that
            notification type in Settings — or by writing to{' '}
            <Tok value={entity.privacyEmail} />.
          </P>
          <H3>Your ARCO rights</H3>
          <P>
            You have the rights of <strong>Acceso, Rectificación, Cancelación y
            Oposición</strong>, and you may also revoke your consent or limit the
            use or disclosure of your data. Exercising them is free; we may only
            recover justified reproduction or delivery costs.
          </P>
          <P>
            Send an ARCO request to <Tok value={entity.privacyEmail} />, stating
            your name, a means of contacting you, proof that you are the account
            holder, a clear description of the data concerned, and which right you
            are exercising. We reply within 20 business days and, if the request is
            granted, act within the following 15 business days. Access, correction
            and deletion are also available immediately in the app — see section
            11.
          </P>
          <P>
            Your data are transferred to and stored in the United States, and
            shared with the providers listed in section 7. If you are not
            satisfied with our response you may complain to the{' '}
            <strong>Secretaría Anticorrupción y Buen Gobierno</strong>, which took
            over data-protection oversight from INAI in 2025.
          </P>
        </>
      ),
    },
    {
      id: 'ca',
      heading: 'Annex C — Canada',
      body: (
        <>
          <P>
            This annex applies if you are in Canada, under PIPEDA and, in Quebec,
            the <em>Act respecting the protection of personal information in the
            private sector</em> as amended by Law 25.
          </P>
          <H3>Privacy Officer</H3>
          <P>
            The person accountable for personal information at Tribys is the{' '}
            <strong><Tok value={entity.privacyOfficer} /></strong> of{' '}
            <Tok value={entity.legalName} />, reachable at{' '}
            <Tok value={entity.privacyEmail} /> or by post at{' '}
            <Tok value={entity.address} />.
          </P>
          <H3>Consent, access and correction</H3>
          <P>
            We collect personal information for the purposes in section 5 and no
            others, and we ask for it in the app at the point it is needed. You may
            withdraw consent for anything optional at any time — verification,
            Spotify, individual notification types — and you may withdraw consent
            altogether by deleting your account. You have the right to access and
            correct your personal information; sections 11 and 12 explain how, and
            you can also ask us directly.
          </P>
          <H3>Automated decisions</H3>
          <P>
            Section 6 lists every decision we make about you by automated means.
            Where such a decision is made exclusively automatically, you may ask
            us which personal information was used, why, and to have it reviewed by
            a person — write to the Privacy Officer.
          </P>
          <H3>Storage outside Canada</H3>
          <P>
            Your personal information is stored and processed in the United States
            and is therefore subject to US law, including lawful access requests by
            US authorities. We assessed this before choosing our providers and rely
            on contractual protections with each of them.
          </P>
          <H3>Complaints</H3>
          <P>
            Complain to our Privacy Officer first — we will investigate and reply
            in writing. If you are not satisfied you may complain to the Office of
            the Privacy Commissioner of Canada, or, in Quebec, to the{' '}
            <Ext href="https://www.cai.gouv.qc.ca/">
              Commission d&apos;accès à l&apos;information
            </Ext>
            .
          </P>
          <P>
            A French translation of this policy is not yet published. If you would
            prefer to deal with us in French, write to the Privacy Officer and we
            will accommodate you.
          </P>
        </>
      ),
    },
  ],
};
